import AppLogo from '@/components/app-logo';
import AppearanceToggleDropdown from '@/components/appearance-dropdown';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { home } from '@/routes';
import { SharedData } from '@/types';
import { Link, usePage } from '@inertiajs/react';
import { type PropsWithChildren } from 'react';

interface AuthLayoutProps {
    name?: string;
    title?: string;
    description?: string;
    status?: string;
    canResetPassword?: boolean;
    canRegister?: boolean;
}

export default function AuthSimpleLayout({
    children,
    title,
    description,
    status,
    canResetPassword,
    canRegister,
}: PropsWithChildren<AuthLayoutProps>) {
    const { name, mobileAppStores, flash } = usePage<SharedData>().props;
    const androidUrl = mobileAppStores?.android ?? null;
    const iosUrl = mobileAppStores?.ios ?? null;
    const apkUrl = mobileAppStores?.apk ?? null;
    const showAppDownloads = Boolean(androidUrl || iosUrl || apkUrl);

    return (
        <div className="font-display flex min-h-screen flex-col bg-white text-green-500 transition-colors duration-200 dark:bg-[#0d1c0f]">
            {/* Top Navigation */}
            <header className="z-20 flex items-center justify-between border-green-500/20 bg-white px-6 py-3 whitespace-nowrap shadow-lg lg:px-10 dark:border-green-900/30 dark:bg-[#152a18]">
                <div className="flex items-center gap-4">
                    <AppLogo />
                    <h2 className="text-lg leading-tight font-bold tracking-[-0.015em] text-green-700 dark:text-white">
                        {name}
                    </h2>
                </div>
                <div className="flex flex-1 justify-end gap-8">
                    <div className="hidden items-center gap-9 md:flex">
                        <AppearanceToggleDropdown />
                    </div>
                    <div className="hidden items-center gap-9 md:flex">
                        <Link
                            href={home()}
                            className="text-sm leading-normal font-medium text-green-700 transition-colors dark:text-green-400"
                        >
                            Home
                        </Link>
                        <a
                            href="#"
                            className="text-sm leading-normal font-medium text-green-700 transition-colors dark:text-green-400"
                        >
                            Menu
                        </a>
                        <a
                            href="#"
                            className="text-sm leading-normal font-medium text-green-700 transition-colors dark:text-green-400"
                        >
                            Locations
                        </a>
                    </div>
                </div>
            </header>

            {/* Main Content Split Layout */}
            <main className="flex flex-1 flex-col lg:flex-row">
                {/* Left Side: Image/Branding */}
                <div className="relative hidden overflow-hidden bg-green-500/10 lg:block lg:w-1/2">
                    {/* Background Image */}
                    <div
                        className="absolute inset-0 bg-cover bg-center opacity-80"
                        style={{
                            backgroundImage: `url('https://lh3.googleusercontent.com/aida-public/AB6AXuA9Ew8tOVn4nidivHKqA5PpA6EtIEYldCxpBlvkosUsCd7MMKK0B1WJ7VJHS_Q8CBXPAzJ2deLY4QMi1f7dNtlf8HYIdB43iHRXrh2iJDeeF-b88JT1MplwZMkaOcVUmXVw15U34XMD9bp0jfmuITMHnTbwPZ3mYgHdoD9mlVbX-Th0h_kQQt7UUKI4rAWTeYsqJDVxP2PkIe65lldAnWLo3m_SRyfPRI-V7yCLMgGK3594xcD1cMjqvKQzX2H9qCWUK0YMq9NWL6E')`,
                        }}
                    ></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-green-950 to-transparent"></div>
                    <div className="relative z-10 flex h-full flex-col justify-end p-16 text-green-500">
                        <div className="mb-6">
                            <div className="mb-4 inline-block rounded-full bg-green-500/20 p-3 text-green-500 backdrop-blur-md">
                                <span className="material-symbols-outlined text-4xl">
                                    eco
                                </span>
                            </div>
                            <h1 className="mb-4 text-4xl leading-tight font-bold text-white">
                                Freshness you can taste,
                                <br />
                                Convenience you can trust.
                            </h1>
                            <p className="max-w-md text-lg text-white">
                                Log in to your e-wallet to enjoy seamless
                                payments, loyalty rewards, and express checkout.
                            </p>
                        </div>
                        <div className="mt-8 flex items-center gap-4">
                            <div className="flex -space-x-3">
                                <img
                                    className="h-10 w-10 rounded-full border-2 border-green-500/30 object-cover"
                                    src="https://lh3.googleusercontent.com/aida-public/AB6AXuBTE9cyA-StnRAuLUiE7cB7roZlC0Y1RWjBcOAxJ7v7I86Qy029f3ioyoeJ9Dn24w4oPqb_pK6sVBvH9VDN6atBYlxYv9tbrONeYfoJPDlz1vQLbf0YlC8ZGXb7WLEq6kxqaqIAjaOSp2xNaVkzWiUOxNM5ca7oq8h0G3TAFgdbNIW9RQLXGZBNOEnoNPVVJuRzKNH5XVYv1pbzdjoL4piEASlTgeeOx6b_d_7N8nyq8ucYKVp7XXERri_zbt6JH91Ubde-G0gw59U"
                                    alt="Customer testimonial"
                                />
                                <img
                                    className="h-10 w-10 rounded-full border-2 border-green-500/30 object-cover"
                                    src="https://lh3.googleusercontent.com/aida-public/AB6AXuB2hFnROziMYoDgx8mSZog_57qS7x_hVLJxedj7HZNJHot4973CUFDHpSYjM5Q5pyZ6LjrFytCc83KWhKaWzsq1CW83Aem8nJK_JoKSiNZUndfE-7z8HzO_emNGbvJa8LOoPVQ6MA6gyPKyBuF8QgeQzLXYmsfvfM-WuwY6FfUds95V7pUY270ROQ0qtBJTYTcsKfK2oe66R_wnhhlkJFbiPlqdqjUqpNCSLCcMG1Xbh-ATmbxhUmXh08uR_bZfa6Z8qbQH8lBsEQ0"
                                    alt="Customer testimonial"
                                />
                                <img
                                    className="h-10 w-10 rounded-full border-2 border-green-500/30 object-cover"
                                    src="https://lh3.googleusercontent.com/aida-public/AB6AXuBD2NVbamvIP4WZoQ_1IkvuQIdYq5vvCNYsIAmd5vZJvlYatiPdktvvJDFlF4c4kKDf8soaQW6XIc9tJQopxPR3Zkgm_t0u-kGmG0OSjC4F3Jl2R9bbBoImaSyV6ikuSybnVqEBudMpo8agFLGlOYVL8fzkCxNEWyt9KdvCapdK4-nG0nG7hJ2XtVJVDXYhHBTgTKMmaLLLEBY4tvYD5KYZQbvMiWU4G4StBcJcjAFpbe6kiypcko6xlDSDePM1sYKgNO_4hzpYQEA"
                                    alt="Customer testimonial"
                                />
                            </div>
                            <div className="text-sm font-medium text-green-500">
                                Joined by 10k+ food lovers
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Side: Login Form */}
                <div className="bg-background-light flex w-full items-center justify-center p-6 sm:p-12 lg:w-1/2 lg:p-20 dark:bg-gray-900">
                    <div className="w-full max-w-lg space-y-8">
                        {/* Header Section */}
                        <div className="text-center lg:text-left">
                            <h2 className="mb-3 text-2xl font-bold tracking-tight text-green-900 dark:text-white">
                                {title}
                            </h2>
                            <p className="text-base leading-relaxed text-green-700 dark:text-white/90">
                                {description}
                            </p>
                        </div>

                        {/* Status Message */}
                        {status && (
                            <div className="text-center text-sm font-medium text-green-600 dark:text-green-400">
                                {status}
                            </div>
                        )}

                        {/* Error Message */}
                        {flash?.error && (
                            <Alert
                                variant="destructive"
                                className="border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-900/20"
                            >
                                <AlertTitle>Login Error</AlertTitle>
                                <AlertDescription>
                                    {flash.error}
                                </AlertDescription>
                            </Alert>
                        )}

                        {/* Form Content */}
                        {children}

                        {/* Footer Call to Action */}
                        {canRegister && (
                            <div className="pt-2 text-center">
                                <p className="text-sm text-green-500/60 dark:text-white">
                                    Don't have an account?{' '}
                                    <Link
                                        href="/register"
                                        className="font-bold text-green-500 transition-colors hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                                    >
                                        Register for free
                                    </Link>
                                </p>
                            </div>
                        )}

                        {showAppDownloads && (
                            <div className="relative overflow-hidden rounded-2xl border border-green-200/70 bg-gradient-to-b from-white to-green-50/40 p-5 shadow-[0_20px_50px_-24px_rgba(21,42,24,0.35)] ring-1 ring-green-500/10 sm:p-6 dark:border-green-800/50 dark:from-[#1a3020] dark:to-[#132518] dark:shadow-[0_24px_60px_-20px_rgba(0,0,0,0.55)] dark:ring-green-400/10">
                                <div
                                    className="pointer-events-none absolute -top-10 -right-6 h-36 w-36 rounded-full bg-emerald-400/25 blur-3xl dark:bg-emerald-500/15"
                                    aria-hidden
                                />
                                <div
                                    className="pointer-events-none absolute -bottom-8 -left-6 h-28 w-28 rounded-full bg-green-600/10 blur-2xl dark:bg-green-500/10"
                                    aria-hidden
                                />

                                <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-5">
                                    <div className="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-green-500 to-emerald-700 text-white shadow-lg shadow-green-900/25 sm:mx-0">
                                        <span
                                            className="material-symbols-outlined text-[26px]"
                                            data-weight="400"
                                        >
                                            smartphones
                                        </span>
                                    </div>
                                    <div className="min-w-0 flex-1 text-center sm:text-left">
                                        <p className="text-[0.65rem] font-bold tracking-[0.2em] text-green-600/90 uppercase dark:text-green-400/90">
                                            Mobile app
                                        </p>
                                        <h3 className="mt-1 text-lg font-bold tracking-tight text-green-950 dark:text-white">
                                            Get {name} on your phone
                                        </h3>
                                        <p className="mt-1.5 text-sm leading-relaxed text-green-800/75 dark:text-green-100/65">
                                            Install the app for #1000 wallet
                                            credits, quicker login, wallet
                                            access, and checkout—same account as
                                            the web.
                                        </p>
                                    </div>
                                </div>

                                <ul className="relative mt-5 flex flex-col gap-2.5">
                                    {androidUrl && (
                                        <li>
                                            <a
                                                href={androidUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="group flex min-h-[3rem] w-full touch-manipulation items-center gap-3 rounded-xl bg-[#0d1410] px-4 py-3 text-left text-white shadow-md transition duration-200 hover:-translate-y-0.5 hover:bg-[#1a2420] hover:shadow-lg active:translate-y-0 dark:bg-[#0a120e] dark:hover:bg-[#142018]"
                                            >
                                                <span
                                                    className="material-symbols-outlined shrink-0 text-[26px] text-green-400 transition-transform duration-200 group-hover:scale-105"
                                                    data-weight="400"
                                                >
                                                    android
                                                </span>
                                                <span className="min-w-0 flex-1">
                                                    <span className="block text-[0.7rem] font-medium tracking-wide text-white/55 uppercase">
                                                        Get it on
                                                    </span>
                                                    <span className="block text-sm font-semibold">
                                                        Google Play
                                                    </span>
                                                </span>
                                                <span
                                                    className="material-symbols-outlined shrink-0 text-white/40 transition group-hover:translate-x-0.5 group-hover:text-white/70"
                                                    data-weight="400"
                                                >
                                                    north_east
                                                </span>
                                            </a>
                                        </li>
                                    )}
                                    {iosUrl && (
                                        <li>
                                            <a
                                                href={iosUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="group flex min-h-[3rem] w-full touch-manipulation items-center gap-3 rounded-xl bg-gradient-to-r from-slate-800 to-slate-950 px-4 py-3 text-left text-white shadow-md transition duration-200 hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0 dark:from-slate-900 dark:to-black"
                                            >
                                                <span
                                                    className="material-symbols-outlined shrink-0 text-[26px] text-sky-300/90 transition-transform duration-200 group-hover:scale-105"
                                                    data-weight="400"
                                                >
                                                    phone_iphone
                                                </span>
                                                <span className="min-w-0 flex-1">
                                                    <span className="block text-[0.7rem] font-medium tracking-wide text-white/55 uppercase">
                                                        Download on the
                                                    </span>
                                                    <span className="block text-sm font-semibold">
                                                        App Store
                                                    </span>
                                                </span>
                                                <span
                                                    className="material-symbols-outlined shrink-0 text-white/40 transition group-hover:translate-x-0.5 group-hover:text-white/70"
                                                    data-weight="400"
                                                >
                                                    north_east
                                                </span>
                                            </a>
                                        </li>
                                    )}
                                    {apkUrl && (
                                        <li>
                                            <a
                                                href={apkUrl}
                                                download
                                                className="group flex min-h-[3rem] w-full touch-manipulation items-center gap-3 rounded-xl border-2 border-emerald-600/35 bg-white/90 px-4 py-3 text-left shadow-sm transition duration-200 hover:-translate-y-0.5 hover:border-emerald-500/60 hover:bg-white hover:shadow-md active:translate-y-0 dark:border-emerald-500/30 dark:bg-emerald-950/25 dark:hover:bg-emerald-950/40"
                                            >
                                                <span
                                                    className="material-symbols-outlined shrink-0 text-[26px] text-emerald-600 dark:text-emerald-400"
                                                    data-weight="400"
                                                >
                                                    install_mobile
                                                </span>
                                                <span className="min-w-0 flex-1">
                                                    <span className="block text-[0.7rem] font-medium tracking-wide text-green-800/55 uppercase dark:text-green-200/50">
                                                        Direct install
                                                    </span>
                                                    <span className="block text-sm font-semibold text-green-950 dark:text-white">
                                                        Download Android APK
                                                    </span>
                                                </span>
                                                <span
                                                    className="material-symbols-outlined shrink-0 text-emerald-600/50 transition group-hover:translate-y-0.5 dark:text-emerald-400/50"
                                                    data-weight="400"
                                                >
                                                    download
                                                </span>
                                            </a>
                                        </li>
                                    )}
                                </ul>
                            </div>
                        )}

                        {/* Security Badge */}
                        <div className="mt-8 flex items-center justify-center gap-2 opacity-60">
                            <span className="material-symbols-outlined text-[16px] text-green-900 dark:text-green-400">
                                lock
                            </span>
                            <span className="text-xs font-medium text-green-900 dark:text-green-400/40">
                                Secured by BloomGuard Encrypt
                            </span>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
